import React, { useEffect, useRef, useState } from "react";
import { CircularProgress, Grid as MuiGrid } from "@mui/material";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { useDispatch, useSelector } from "react-redux";

import {
     retailSalesClearUpdateWinnerResult,
     retailSalesUpdateWinnerRequest,
} from "../../../../../_redux/_actions";
import { retailSalesConstants } from "../../../../../_redux/_constants/retailSales.constants";

/**
 * Corrects a winner that was entered wrongly: their name, their dealer or their role.
 * Email is not editable because it is the account's login identity, and the reward is not
 * editable because it exists only as a points transaction that cannot be amended.
 */
export default function EditRetailWinner(props) {
     const { winner, dealerOptions, jobRoleOptions, onDealerSearch, onSaved, onClose } = props;

     const actionDispatch = useDispatch();
     const updateResult = useSelector(
          (state) => state?.retailSales?.updateWinnerResult,
     );

     const [form, setForm] = useState({
          firstName: winner?.firstName || "",
          lastName: winner?.lastName || "",
          dealer: null,
          jobRole: null,
     });
     const [submitted, setSubmitted] = useState(false);

     // The save is in flight while its request action sits in the pending list the loading
     // middleware maintains. Driving the button from that rather than a local flag means a failed
     // call clears the spinner too, instead of leaving it stuck on "Saving...".
     const isSaving = useSelector((state) =>
          (state?.loading?.pendingRequests || []).includes(
               retailSalesConstants.RETAIL_SALES_UPDATE_WINNER_REQUEST,
          ),
     );

     // Preselect the dropdowns from the winner's current dealer and role. The report returns the
     // raw ids alongside the display names for exactly this.
     useEffect(() => {
          setForm((previous) => ({
               ...previous,
               dealer:
                    previous.dealer ||
                    (dealerOptions || []).find(
                         (option) => option.id === winner?.dealershipId,
                    ) ||
                    null,
               jobRole:
                    previous.jobRole ||
                    (jobRoleOptions || []).find(
                         (option) => option.id === winner?.jobRoleId,
                    ) ||
                    null,
          }));
     }, [dealerOptions, jobRoleOptions, winner]);

     useEffect(() => {
          return () => {
               actionDispatch(retailSalesClearUpdateWinnerResult());
          };
     }, [actionDispatch]);

     // The BFF unwraps the envelope, so a successful save arrives as the updated winner itself.
     // Anything else means the call did not succeed.
     const saveSucceeded = Boolean(updateResult?.participantId);

     // Close the form as soon as the save finishes, whichever way it went. A success refreshes the
     // grid; a failure has already been reported by the global error notification, so leaving the
     // form open on "Saving..." would just strand the user.
     const wasSaving = useRef(false);

     useEffect(() => {
          const saveFinished = wasSaving.current && !isSaving;
          wasSaving.current = isSaving;

          if (!submitted || !saveFinished) {
               return;
          }

          if (saveSucceeded && onSaved) {
               onSaved();
               return;
          }

          if (!saveSucceeded && onClose) {
               onClose();
          }
     }, [submitted, isSaving, saveSucceeded, onSaved, onClose]);

     const setField = (field, value) =>
          setForm((previous) => ({ ...previous, [field]: value }));

     const canSubmit =
          form.firstName.trim() &&
          form.lastName.trim() &&
          form.dealer &&
          form.jobRole &&
          !isSaving;

     const submit = () => {
          setSubmitted(true);
          actionDispatch(retailSalesClearUpdateWinnerResult());

          actionDispatch(
               retailSalesUpdateWinnerRequest({
                    participantId: winner.participantId,
                    firstName: form.firstName.trim(),
                    lastName: form.lastName.trim(),
                    dealershipId: form.dealer.id,
                    jobRoleId: form.jobRole.id,
               }),
          );
     };

     // Every message shown here comes from the backend response. A failed call closes the form
     // instead, so there is no local error text to invent.
     const responseMessage = saveSucceeded ? updateResult.message : "";

     return (
          <MuiGrid container spacing={2} sx={{ maxWidth: "900px" }}>
               <MuiGrid item xs={12}>
                    {/* Email is shown for context only. It is the account's login identity. */}
                    <label style={{ marginRight: "8px", fontWeight: 600 }}>
                         Email
                    </label>
                    <span>{winner?.email}</span>
               </MuiGrid>
               <MuiGrid item xs={12} lg={6}>
                    <Input
                         style={{ width: "100%" }}
                         label="First name"
                         value={form.firstName}
                         onChange={(event) =>
                              setField("firstName", event.value)
                         }
                    />
               </MuiGrid>
               <MuiGrid item xs={12} lg={6}>
                    <Input
                         style={{ width: "100%" }}
                         label="Last name"
                         value={form.lastName}
                         onChange={(event) => setField("lastName", event.value)}
                    />
               </MuiGrid>
               <MuiGrid item xs={12} lg={6}>
                    <DropDownList
                         style={{ width: "100%" }}
                         label="Dealer"
                         data={dealerOptions}
                         textField="label"
                         dataItemKey="id"
                         value={form.dealer}
                         filterable={true}
                         onFilterChange={(event) =>
                              onDealerSearch(
                                   event.filter ? event.filter.value : "",
                              )
                         }
                         onChange={(event) => setField("dealer", event.value)}
                    />
               </MuiGrid>
               <MuiGrid item xs={12} lg={6}>
                    <DropDownList
                         style={{ width: "100%" }}
                         label="Role"
                         data={jobRoleOptions}
                         textField="name"
                         dataItemKey="id"
                         value={form.jobRole}
                         onChange={(event) => setField("jobRole", event.value)}
                    />
               </MuiGrid>
               <MuiGrid item xs={12}>
                    <Button
                         type="button"
                         themeColor="primary"
                         disabled={!canSubmit}
                         onClick={submit}
                    >
                         {isSaving ? "Saving..." : "Save changes"}
                    </Button>
                    {isSaving && (
                         <CircularProgress
                              size={18}
                              sx={{ ml: "12px", verticalAlign: "middle" }}
                         />
                    )}
                    {responseMessage && (
                         <span
                              style={{
                                   marginLeft: "12px",
                                   color: "#2e7d32",
                              }}
                         >
                              {responseMessage}
                         </span>
                    )}
               </MuiGrid>
          </MuiGrid>
     );
}
